import { ACTIVITY_TYPES } from '../../activities/types'
import { resolveFollowUpChannel } from '../../followUps/channel'
import { isUuid, type BuildFollowUpPayloadInput } from '../../followUps/schema'
import { buildActivityPayload, mapActivityRow } from '../mappers/activities'
import {
  buildFollowUpPayload,
  buildFollowUpUpdatePayload,
  mapFollowUpRow,
} from '../mappers/followUps'
import { buildProspectPayload } from '../mappers/prospects'
import { buildWorkflowPayload } from '../mappers/workflow'
import { ActivitiesRepository } from '../repositories/activities.repository'
import { FollowUpsRepository } from '../repositories/followUps.repository'
import { ProspectsRepository } from '../repositories/prospects.repository'
import { WorkflowRepository } from '../repositories/workflow.repository'
import type { ActivityRow, FollowUpRow } from '../types/database.types'
import { AdminService } from './admin.service'
import { unwrap } from './result'

export type FollowUpScheduleStage =
  | 'follow_up_lookup'
  | 'follow_up_insert'
  | 'follow_up_activity_lookup'
  | 'follow_up_activity_insert'
  | 'prospect_follow_up_update'
  | 'workflow_follow_up_update'

export class FollowUpScheduleError extends Error {
  public constructor(
    public readonly diagnostic: {
      stage: FollowUpScheduleStage
      table: 'follow_ups' | 'activities' | 'prospects' | 'prospect_workflows'
      operation: 'select' | 'insert' | 'update'
      code?: string
      cause: unknown
      record?: {
        prospectId: string
        profileId?: string
        scheduledAt: string
        followUpId?: string
        channel?: string
      }
    },
  ) {
    super(
      `Etapa: ${diagnostic.stage}. Recurso: ${diagnostic.table}. Operación: ${diagnostic.operation}.${diagnostic.code ? ` Código: ${diagnostic.code}.` : ''}`,
    )
    this.name = 'FollowUpScheduleError'
  }
}

export interface ScheduleWorkflowFollowUpInput {
  prospectId: string
  scheduledAt: string
  notes?: string
  followUpReason?: string | null
  reason?: string | null
  channel?: string | null
  recommendedChannel?: string | null
  assignedTo?: string
  profileId?: string
  stepCode?: string
}

export interface ScheduleWorkflowFollowUpResult {
  followUpId: string
  activityId: string
  reusedFollowUp: boolean
  reusedActivity: boolean
}

export interface UpdatePendingFollowUpInput {
  id: string
  scheduledAt: string
  channel: string
  reason?: string | null
}

const scheduleRequests = new Map<
  string,
  Promise<ScheduleWorkflowFollowUpResult>
>()

export const FollowUpService = {
  async listPending() {
    return unwrap(await FollowUpsRepository.listPending())
  },

  async getPending(prospectId: string): Promise<FollowUpRow | null> {
    const pending = unwrap(
      await FollowUpsRepository.findPendingByProspect(prospectId),
    )
    return pending ? mapFollowUpRow(pending) : null
  },

  async updatePending(input: UpdatePendingFollowUpInput): Promise<FollowUpRow> {
    const updated = mapFollowUpRow(
      unwrap(
        await FollowUpsRepository.updatePending(
          input.id,
          buildFollowUpUpdatePayload(input),
        ),
      ),
    )

    await updateFollowUpContext(
      updated.prospect_id,
      updated.scheduled_at,
      updated.assigned_to,
    )
    return updated
  },

  async complete(input: {
    id: string
    outcome: string
    createNext: boolean
    nextDate: string | null
  }): Promise<void> {
    unwrap(await FollowUpsRepository.complete(input))
  },

  async cancel(id: string): Promise<void> {
    await this.complete({
      id,
      outcome: 'Cancelado',
      createNext: false,
      nextDate: null,
    })
  },

  async schedule(
    input: ScheduleWorkflowFollowUpInput,
  ): Promise<ScheduleWorkflowFollowUpResult> {
    const profileId = await resolveProfileId(input)
    const scheduledAt = normalizeDate(input.scheduledAt)
    const requestKey = input.prospectId
    const current = scheduleRequests.get(requestKey)
    if (current) return current

    const request = runSchedule({ ...input, profileId, scheduledAt }).finally(
      () => {
        scheduleRequests.delete(requestKey)
      },
    )
    scheduleRequests.set(requestKey, request)
    return request
  },

  async retryActivity(
    input: Omit<
      ScheduleWorkflowFollowUpInput,
      'stepCode' | 'recommendedChannel'
    > & { channel: string },
  ) {
    const profileId = await resolveProfileId(input)
    const scheduledAt = normalizeDate(input.scheduledAt)
    const activity = await getOrCreateActivity({
      prospectId: input.prospectId,
      profileId,
      scheduledAt,
      channel: input.channel,
      reason: reasonFor(input),
    })

    await updateFollowUpContext(input.prospectId, scheduledAt, profileId)
    return { activityId: activity.row.id, reusedActivity: activity.reused }
  },

  async createOrReusePending(
    input: BuildFollowUpPayloadInput,
  ): Promise<{ followUp: FollowUpRow; reusedFollowUp: boolean }> {
    const followUp = await getOrCreateFollowUp({
      ...input,
      scheduledAt: normalizeDate(input.scheduledAt),
    })

    return {
      followUp: followUp.row,
      reusedFollowUp: followUp.reused,
    }
  },

  async createOrReuseActivity(
    payload: Record<string, unknown>,
  ): Promise<{ activity: ActivityRow; reusedActivity: boolean }> {
    const prospectId =
      typeof payload.prospect_id === 'string' ? payload.prospect_id : ''
    const profileId = typeof payload.user_id === 'string' ? payload.user_id : ''
    const scheduledAt =
      typeof payload.next_follow_up_at === 'string'
        ? normalizeDate(payload.next_follow_up_at)
        : ''
    const channel = typeof payload.channel === 'string' ? payload.channel : ''

    if (!prospectId || !profileId || !scheduledAt || !channel) {
      throw new Error(
        'La actividad de seguimiento no contiene los datos requeridos.',
      )
    }

    const activity = await getOrCreateActivity({
      prospectId,
      profileId,
      scheduledAt,
      channel,
      reason:
        typeof payload.description === 'string' ? payload.description : null,
    })

    return {
      activity: activity.row,
      reusedActivity: activity.reused,
    }
  },

  async updateProspectNextFollowUp(
    prospectId: string,
    scheduledAt: string,
  ): Promise<void> {
    unwrap(
      await ProspectsRepository.update(
        prospectId,
        buildProspectPayload({ next_follow_up_at: normalizeDate(scheduledAt) }),
      ),
    )
  },

  async updateWorkflowAfterFollowUp(
    prospectId: string,
    scheduledAt: string,
  ): Promise<void> {
    unwrap(
      await WorkflowRepository.updateAfterFollowUp(
        prospectId,
        buildWorkflowPayload({
          next_action_due_at: normalizeDate(scheduledAt),
          state: 'waiting',
        }) as { next_action_due_at: string; state: 'waiting' },
      ),
    )
  },
}

async function runSchedule(
  input: ScheduleWorkflowFollowUpInput & {
    profileId: string
    scheduledAt: string
  },
): Promise<ScheduleWorkflowFollowUpResult> {
  const existing = await findPendingByProspect(input.prospectId)
  const followUp = existing
    ? { row: existing, reused: true }
    : await getOrCreateFollowUp({
        prospectId: input.prospectId,
        assignedTo: input.profileId,
        scheduledAt: input.scheduledAt,
        channel: await resolveChannel(input),
        reason: reasonFor(input),
      })
  const activity = await getOrCreateActivity({
    prospectId: followUp.row.prospect_id,
    profileId: followUp.row.assigned_to,
    scheduledAt: followUp.row.scheduled_at,
    channel: followUp.row.channel,
    reason: followUp.row.reason,
  })

  await updateFollowUpContext(
    followUp.row.prospect_id,
    followUp.row.scheduled_at,
    followUp.row.assigned_to,
  )

  return {
    followUpId: followUp.row.id,
    activityId: activity.row.id,
    reusedFollowUp: followUp.reused,
    reusedActivity: activity.reused,
  }
}

async function getOrCreateFollowUp(input: BuildFollowUpPayloadInput) {
  const existing = await findPendingByProspect(input.prospectId)
  if (existing) return { row: existing, reused: true }

  const payload = buildFollowUpPayload(input)
  const created = await FollowUpsRepository.create(payload)
  if (!created.error) {
    return { row: mapFollowUpRow(created.data), reused: false }
  }

  if (isPendingFollowUpConflict(created.error)) {
    const concurrent = await findPendingByProspect(input.prospectId)
    if (concurrent) return { row: concurrent, reused: true }
  }

  throw new Error(created.error.message)
}

async function findPendingByProspect(prospectId: string) {
  const result = await FollowUpsRepository.findPendingByProspect(prospectId)
  if (result.error) throw new Error(result.error.message)
  return result.data ? mapFollowUpRow(result.data) : null
}

function isPendingFollowUpConflict(error: { code?: string; message: string }) {
  return (
    error.code === '23505' ||
    error.code === '409' ||
    error.message.includes('follow_ups_unique_pending_schedule_idx') ||
    error.message.includes('duplicate key')
  )
}

async function getOrCreateActivity(input: {
  prospectId: string
  profileId: string
  scheduledAt: string
  channel: string
  reason: string | null
}): Promise<{ row: ActivityRow; reused: boolean }> {
  const existing = unwrap(
    await ActivitiesRepository.findFollowUpActivity(
      input.prospectId,
      input.scheduledAt,
    ),
  )
  if (existing) return { row: mapActivityRow(existing), reused: true }

  const payload = buildActivityPayload({
    prospect_id: input.prospectId,
    user_id: input.profileId,
    activity_type: ACTIVITY_TYPES.FOLLOW_UP,
    channel: input.channel,
    occurred_at: new Date().toISOString(),
    description: input.reason || 'Seguimiento programado',
    result: 'scheduled',
    received_response: false,
    next_follow_up_at: input.scheduledAt,
  })
  return {
    row: mapActivityRow(unwrap(await ActivitiesRepository.create(payload))),
    reused: false,
  }
}

async function updateFollowUpContext(
  prospectId: string,
  scheduledAt: string,
  profileId: string,
) {
  try {
    unwrap(
      await ProspectsRepository.update(
        prospectId,
        buildProspectPayload({ next_follow_up_at: scheduledAt }),
      ),
    )
  } catch (cause) {
    throw new FollowUpScheduleError({
      stage: 'prospect_follow_up_update',
      table: 'prospects',
      operation: 'update',
      cause,
      record: { prospectId, profileId, scheduledAt },
    })
  }

  try {
    unwrap(
      await WorkflowRepository.updateAfterFollowUp(
        prospectId,
        buildWorkflowPayload({
          next_action_due_at: scheduledAt,
          state: 'waiting',
        }) as {
          next_action_due_at: string
          state: 'waiting'
        },
      ),
    )
  } catch (cause) {
    throw new FollowUpScheduleError({
      stage: 'workflow_follow_up_update',
      table: 'prospect_workflows',
      operation: 'update',
      cause,
      record: { prospectId, profileId, scheduledAt },
    })
  }
}

async function resolveProfileId(
  input: Pick<ScheduleWorkflowFollowUpInput, 'assignedTo' | 'profileId'>,
) {
  const supplied = input.profileId ?? input.assignedTo
  if (supplied && isUuid(supplied)) return supplied

  const user = await AdminService.getCurrentUser()
  if (!user || !isUuid(user.id))
    throw new Error('No fue posible verificar una sesión activa válida.')
  return user.id
}

async function resolveChannel(input: ScheduleWorkflowFollowUpInput) {
  const [lastActivity, prospect] = await Promise.all([
    ActivitiesRepository.getLatestChannel(input.prospectId),
    ProspectsRepository.getContactChannels(input.prospectId),
  ])

  const channel = resolveFollowUpChannel({
    selectedChannel: input.channel,
    recommendedChannel: input.recommendedChannel,
    lastActivity: unwrap(lastActivity) as {
      activity_type?: string | null
      channel?: string | null
    } | null,
    prospect: unwrap(prospect) as {
      whatsapp?: string | null
      phone?: string | null
      instagram_url?: string | null
      facebook_url?: string | null
      email?: string | null
    } | null,
  })

  if (!channel) {
    throw new Error(
      'Elegí el canal del seguimiento: WhatsApp, Instagram, Facebook, Correo o Teléfono.',
    )
  }

  return channel
}

function normalizeDate(value: string) {
  const parsed = new Date(value)
  if (!value || Number.isNaN(parsed.getTime()))
    throw new Error('La fecha programada no es válida.')
  return parsed.toISOString()
}

function reasonFor(
  input: Pick<
    ScheduleWorkflowFollowUpInput,
    'notes' | 'followUpReason' | 'reason'
  >,
) {
  return (
    input.followUpReason?.trim() ||
    input.notes?.trim() ||
    input.reason?.trim() ||
    null
  )
}
